import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { Dropdown, StatisticsChart, DialogModal } from '@/components';
import { useApi } from '@/api/useApi.tsx';
import { convertMsToHoursFloat, convertMsToTimeWithoutSeconds, objectArraySort } from '@/utility';
import { AvailableCurriculumFilterValue } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { Tooltip, TooltipRefProps } from 'react-tooltip';
import LessonItem from './LessonItem.tsx';
import { Period, SortType, SortState } from './student-performance.types.ts';
import CustomPlaceholder from '@/components/custom-placeholder';
import { Desktop, Mobile } from '@/utility/responsive';
import clsx from 'clsx';

import '@/assets/styles/components/student-performance.scss';

const periods: Period[] = [
	{ id: 'DAILY', name: 'Günlük' },
	{ id: 'WEEKLY', name: 'Haftalık' },
	{ id: 'MONTHLY', name: 'Aylık' }
];

const sortDirections = {
	ASC: 'asc',
	DESC: 'desc'
} as const;

const directions = {
	DOWN: 'down',
	UP: 'up'
} as const;

interface StudentPerformanceProps {
	studentId: string;
	gradeId: number;
}

const StudentPerformance: React.FC<StudentPerformanceProps> = ({ studentId, gradeId }) => {
	const [selectedLesson, setSelectedLesson] = useState<AvailableCurriculumFilterValue>({
		id: undefined,
		name: 'Tüm Dersler ve Denemeler'
	});
	const [selectedPeriod, setSelectedPeriod] = useState(periods[2]);
	const [sortState, setSortState] = useState<SortState>({
		field: null,
		direction: sortDirections.ASC
	});
	const [startDate, setStartDate] = useState(dayjs().subtract(8, 'month').format());
	const [endDate, setEndDate] = useState(dayjs().format());
	const [startDateQuestion, setStartDateQuestion] = useState(dayjs().subtract(8, 'month').format());
	const [endDateQuestion, setEndDateQuestion] = useState(dayjs().format());
	const [isDialogModalOpen, setIsDialogModalOpen] = useState(false);
	const [isSortModalOpen, setIsSortModalOpen] = useState(false);

	const tooltipRef = React.useRef<TooltipRefProps>(null);
	const { StudentStatisticControllerApi, MeasurementEvaluationControllerApi } = useApi();

	const { data: filterData, isLoading: loadingFilterData } = useQueryProvider(
		[QUERY_KEYS.getAvailableFilters, studentId],
		() => StudentStatisticControllerApi.getAvailableFilters(parseInt(studentId)),
		{
			select: ({ data }) => data.data,
			enabled: !!studentId,
			staleTime: Infinity,
			refetchOnWindowFocus: false
		}
	);

	const { data: contentStatistics, isLoading: loadingVideoStats } = useQueryProvider(
		[QUERY_KEYS.getStudentContentStatistics, studentId, startDate, endDate, selectedPeriod?.id || '', selectedLesson?.id?.toString() || 'ALL'],
		() =>
			StudentStatisticControllerApi.getStudentContentStatistics(parseInt(studentId), gradeId, startDate, endDate, selectedPeriod?.id, selectedLesson?.id === 0 ? undefined : selectedLesson?.id),
		{
			select: ({ data }) => data.data,
			enabled: !!studentId,
			refetchOnWindowFocus: false
		}
	);

	const { data: questionStatistics, isLoading: loadingQuestionsStats } = useQueryProvider(
		[QUERY_KEYS.getStudentItemStatistics, studentId, startDateQuestion, endDateQuestion, selectedPeriod?.id || '', selectedLesson?.id?.toString() || 'ALL'],
		() =>
			StudentStatisticControllerApi.getStudentItemStatistics(
				parseInt(studentId),
				gradeId,
				startDateQuestion,
				endDateQuestion,
				selectedPeriod?.id,
				selectedLesson?.id === 0 ? undefined : selectedLesson?.id
			),
		{
			select: ({ data }) => data.data,
			enabled: !!studentId,
			refetchOnWindowFocus: false
		}
	);

	const { data: curriculumMeasurements, isLoading: loadingLessons } = useQueryProvider(
		[QUERY_KEYS.getStudentCurriculumMeasurement, studentId],
		() => MeasurementEvaluationControllerApi.getStudentCurriculumMeasurement(parseInt(studentId)),
		{
			select: ({ data }) => data.data,
			enabled: !!studentId,
			refetchOnWindowFocus: false
		}
	);

	const lessons = useMemo(() => {
		const curriculumsFilterOpts: AvailableCurriculumFilterValue[] = [
			{
				name: 'Tüm Dersler ve Denemeler',
				id: 0
			}
		];

		if (!loadingFilterData && filterData?.availableCurriculumFilterValues?.length && filterData?.availableCurriculumFilterValues?.length > 0) {
			// eslint-disable-next-line prefer-spread
			curriculumsFilterOpts.push.apply(
				curriculumsFilterOpts,
				filterData?.availableCurriculumFilterValues.map(({ id, name }) => ({
					id,
					name
				}))
			);
		}
		return curriculumsFilterOpts;
	}, [filterData, loadingFilterData]);

	const videoStats = useMemo(() => {
		if (contentStatistics?.contentDataList) {
			if (contentStatistics?.contentDataList?.length === 0) {
				return [
					{
						sessionDate: '',
						value: 0,
						info: `0 saat, 0 dakika, 0 saniye`
					}
				];
			} else {
				return objectArraySort(
					contentStatistics?.contentDataList?.map(item => {
						const valueOfTime = convertMsToTimeWithoutSeconds(item?.totalDurationInMillis);

						let info = `${valueOfTime.minutes} dakika (${item?.totalContentCount} video)`;

						if (valueOfTime.hours > 0) {
							info = `${valueOfTime.hours} saat, ${info}`;
						}

						return {
							sessionDate: item?.sessionDate,
							value: convertMsToHoursFloat(item?.totalDurationInMillis),
							info
						};
					}) || [],
					'sessionDate',
					sortDirections.ASC
				);
			}
		}
	}, [contentStatistics]);

	const solvedQuestions = useMemo(() => {
		if (questionStatistics?.itemDataList) {
			if (questionStatistics?.itemDataList?.length === 0) {
				return [
					{
						sessionDate: '',
						value: 0,
						value2: 0,
						value3: 0,
						info: `0 Soru, 0 Doğru, 0 Yanlış, 0 Boş`
					}
				];
			} else {
				return objectArraySort(
					questionStatistics?.itemDataList?.map(item => {
						return {
							sessionDate: item?.sessionDate,
							value: item?.emptyItemCount,
							value2: item?.falseItemCount,
							value3: item?.trueItemCount,
							info: `${item?.itemCount} Soru: ${item?.trueItemCount} Doğru, ${item?.falseItemCount} Yanlış, ${item?.emptyItemCount} Boş`
						};
					}) || [],
					'sessionDate',
					sortDirections.ASC
				);
			}
		}
	}, [questionStatistics]);

	const handleLessonSelect = (lesson: AvailableCurriculumFilterValue) => {
		setSelectedLesson(lesson);
	};

	const handlePeriodSelect = (period: Period) => {
		setSelectedPeriod(period);
	};

	const handleSort = (field: SortType): void => {
		if (sortState.field === field && sortState.direction === sortDirections.ASC) {
			setSortState(prevState => ({
				...prevState,
				direction: prevState.direction === sortDirections.ASC ? sortDirections.DESC : sortDirections.ASC
			}));
			return;
		}
		if (sortState.field !== field) {
			setSortState({
				field,
				direction: sortDirections.ASC
			});
			return;
		}

		setSortState({
			field: null,
			direction: sortDirections.ASC
		});
	};

	const handleSortOptionSelect = (field: SortType, direction: (typeof sortDirections)[keyof typeof sortDirections]) => {
		setSortState({
			field,
			direction
		});
		setIsSortModalOpen(false);
	};

	const getSortIcon = (field: SortType): JSX.Element => {
		if (sortState.field === field) {
			return <i className={`icons-alt-arrow-${sortState.direction === sortDirections.ASC ? directions.DOWN : directions.UP}`} />;
		}
		return <i className="icons-alt-arrow-down half-transparent" />;
	};

	const sortedCurriculumMeasurements = useMemo(() => {
		if (!curriculumMeasurements?.length || !sortState.field) {
			return curriculumMeasurements;
		}

		return [...curriculumMeasurements].sort((a, b) => {
			const multiplier = sortState.direction === sortDirections.ASC ? -1 : 1;

			switch (sortState.field) {
				case SortType.CURRICULUM: {
					return (a.curriculumName?.localeCompare(b.curriculumName || '') || 0) * multiplier;
				}
				case SortType.COMPLETION: {
					const aCompletion = Math.ceil(a.measurement?.completion?.progressInfo?.completedContent || 0);
					const bCompletion = Math.ceil(b.measurement?.completion?.progressInfo?.completedContent || 0);
					return (aCompletion - bCompletion) * multiplier;
				}
				case SortType.SUCCESS_RATE: {
					const aRate = Math.ceil((a.measurement?.performance?.successRateStatistics?.successRate || 0) * 100);
					const bRate = Math.ceil((b.measurement?.performance?.successRateStatistics?.successRate || 0) * 100);
					return (aRate - bRate) * multiplier;
				}
				default:
					return 0;
			}
		});
	}, [curriculumMeasurements, sortState]);

	const HowtoCalculateTooltip = () => {
		return (
			<div className="how-to-calculate-info-dialog">
				<div className="how-to-calculate-info-dialog-header">
					<div className="how-to-calculate-info-dialog-title-wrapper">
						<i className="icons-info-circle" />
						<h3 className="how-to-calculate-info-dialog-title">Konu anlatımı tamamlama ve soru başarı yüzdesi nasıl hesaplanır?</h3>
						<button
							className="close-button"
							onClick={() => {
								tooltipRef?.current?.close();
							}}
							aria-label="Close">
							<i className="icons-close" />
						</button>
					</div>
				</div>
				<div className="how-to-calculate-info-dialog-content">
					<p className="how-to-calculate-info-dialog-description">
						Bir videonun en az yarısı aktif şekilde izlendikten sonra o video Konu Anlatımı yüzdesine tamamlanmış olarak eklenir. Soru başarı yüzden, doğru cevapladığın soru sayısının
						çözdüğün soru sayısına oranını gösterir.
					</p>
				</div>
			</div>
		);
	};

	return (
		<div className="student-performance">
			<div className="student-performance__stats-title">Video ve Soru İstatistikleri</div>

			<div className="student-performance__tables">
				<div className="student-performance__filters">
					<div className="student-performance__filters-dropdown">
						<Dropdown
							options={lessons}
							selectedOption={selectedLesson}
							onOptionSelect={handleLessonSelect}
							placeholder="Ders seçin"
							size="medium"
							fullWidth={false}
						/>
						<Dropdown
							options={periods}
							selectedOption={selectedPeriod}
							onOptionSelect={handlePeriodSelect}
							placeholder="Periyot Seçin"
							size="medium"
							fullWidth={false}
						/>
					</div>
				</div>

				<div className="student-performance__chart">
					<div className="student-performance__stats-title">İzlenen Video Süresi</div>
					<CustomPlaceholder
						ready={!loadingVideoStats}
						type="rect"
						height={300}
						className="student-performance__chart-placeholder">
						<StatisticsChart
							type="line"
							data={videoStats || []}
							period={selectedPeriod?.id}
							unit="dk"
							height={300}
							endDate={endDate}
							onDateChange={({ start, end }) => {
								setStartDate(start);
								setEndDate(end);
							}}
							barColors={['#24B0A2', 'var(--surface-6366f1)', '#EC4899']}
						/>
					</CustomPlaceholder>
				</div>

				<div className="student-performance__solved-questions">
					<div className="student-performance__solved-questions-header">
						<div className="student-performance__solved-questions-header-title">Çözülen Soru Sayısı</div>
						<div className="student-performance__solved-questions-header-highlight">
							<div className="student-performance__solved-questions-header-highlight-dot student-performance__solved-questions-header-highlight-dot--correct"></div>
							Doğru Cevap
						</div>
						<div className="student-performance__solved-questions-header-highlight">
							<div className="student-performance__solved-questions-header-highlight-dot student-performance__solved-questions-header-highlight-dot--incorrect"></div>
							Yanlış Cevap
						</div>
						<div className="student-performance__solved-questions-header-highlight">
							<div className="student-performance__solved-questions-header-highlight-dot student-performance__solved-questions-header-highlight-dot--blog"></div>
							Boş
						</div>
					</div>
					<div className="student-performance__solved-questions-header-total">
						<div className="student-performance__solved-questions-header-total-dot" />
						Toplam Çözülen Soru: <span>{questionStatistics?.totalSolvedItemCount || 0}</span>
					</div>
					<CustomPlaceholder
						ready={!loadingQuestionsStats}
						type="rect"
						height={300}
						className="student-performance__solved-questions-placeholder">
						<StatisticsChart
							type="mixedBar"
							data={solvedQuestions || []}
							period={selectedPeriod?.id}
							unit={'saat'}
							height={300}
							endDate={endDateQuestion}
							onDateChange={({ start, end }) => {
								setStartDateQuestion(start);
								setEndDateQuestion(end);
							}}
						/>
					</CustomPlaceholder>
				</div>
			</div>
			<div className="student-performance__lessons">
				<div className="student-performance__lessons-header">
					<div className="student-performance__lessons-header-col-lessons">
						<div className="student-performance__lessons-header-col-lessons-title">Dersler</div>
						<div
							className="student-performance__lessons-header-col-lessons-description"
							onClick={() => {
								tooltipRef?.current?.open();
								setIsDialogModalOpen(true);
							}}
							id="how-to-calculate-tooltip">
							<i className="icons-info-circle" />
							Nasıl Hesaplanır?
						</div>
						<Tooltip
							id={'#how-to-calculate-tooltip'}
							anchorSelect={'#how-to-calculate-tooltip'}
							className="how-to-calculate-tooltip-container student-performance__desktop-tooltip"
							ref={tooltipRef}
							place={'bottom-start'}
							clickable={true}
							openOnClick={true}
							render={() => <HowtoCalculateTooltip />}
						/>
					</div>
					<Desktop>
						<div
							className="student-performance__lessons-header-col-curriculum sortable"
							onClick={() => handleSort(SortType.COMPLETION)}>
							<span>Konu Anlatımı</span>
							{getSortIcon(SortType.COMPLETION)}
						</div>
						<div>
							<div
								className="student-performance__lessons-header-col-success-rate sortable"
								onClick={() => handleSort(SortType.SUCCESS_RATE)}>
								<span>Soru Başarısı</span>
								{getSortIcon(SortType.SUCCESS_RATE)}
							</div>
						</div>
					</Desktop>
					<Mobile>
						<div className="student-performance__lessons-header-sort">
							<button
								className="student-performance__lessons-header-sort-button"
								onClick={() => setIsSortModalOpen(true)}>
								<i className="icons-sort-from-top-to-bottom" />
								Sırala
							</button>
						</div>
					</Mobile>
				</div>
				{loadingLessons ? (
					Array.from({ length: 3 }, (_, index) => (
						<CustomPlaceholder
							key={`placeholder-${index}`}
							ready={false}
							type="rect"
							height={80}
							className="student-performance__lesson-placeholder">
							<div />
						</CustomPlaceholder>
					))
				) : sortedCurriculumMeasurements?.length ? (
					sortedCurriculumMeasurements?.map(lesson => (
						<CustomPlaceholder
							key={lesson.curriculumId}
							ready={true}
							type="rect"
							height={80}
							className="student-performance__lesson-placeholder">
							<LessonItem
								key={lesson.curriculumId}
								lesson={lesson}
								studentId={studentId}
							/>
						</CustomPlaceholder>
					))
				) : (
					<div className="student-performance__lessons-empty">
						<div className="student-performance__lessons-empty-icon">
							<i className="icons-book-closed" />
						</div>
						<div className="student-performance__lessons-empty-title">Henüz Ders Kaydı Bulunmuyor</div>
						<div className="student-performance__lessons-empty-description">
							Öğrencinin henüz izlediği video veya çözdüğü soru bulunmuyor. Öğrencinin performansını görebilmek için önce ders içeriklerini tamamlaması gerekiyor.
						</div>
					</div>
				)}
			</div>

			<DialogModal
				isOpen={isDialogModalOpen}
				onClose={() => setIsDialogModalOpen(false)}
				variant="info"
				title="Konu anlatımı tamamlama ve soru başarı yüzdesi nasıl hesaplanır?"
				primaryButtonText="Kapat"
				onPrimaryClick={() => setIsDialogModalOpen(false)}
				className="student-performance__mobile-dialog"
				mobileBottomSheet={true}>
				<p>
					Bir videonun en az yarısı aktif şekilde izlendikten sonra o video Konu Anlatımı yüzdesine tamamlanmış olarak eklenir. Soru başarı yüzden, doğru cevapladığın soru sayısının çözdüğün
					soru sayısına oranını gösterir.
				</p>
			</DialogModal>

			<DialogModal
				isOpen={isSortModalOpen}
				onClose={() => setIsSortModalOpen(false)}
				variant="info"
				title="Sıralama Seçenekleri"
				primaryButtonText="Kapat"
				onPrimaryClick={() => setIsSortModalOpen(false)}
				className="student-performance__mobile-dialog"
				mobileBottomSheet={true}>
				<div className="student-performance__sort-options">
					<button
						className={clsx('student-performance__sort-option', {
							active: sortState.field === SortType.COMPLETION && sortState.direction === sortDirections.ASC
						})}
						onClick={() => handleSortOptionSelect(SortType.COMPLETION, sortDirections.ASC)}>
						Konu Anlatımı (Çoktan Aza)
					</button>
					<button
						className={clsx('student-performance__sort-option', {
							active: sortState.field === SortType.COMPLETION && sortState.direction === sortDirections.DESC
						})}
						onClick={() => handleSortOptionSelect(SortType.COMPLETION, sortDirections.DESC)}>
						Konu Anlatımı (Azdan Çoka)
					</button>
					<button
						className={clsx('student-performance__sort-option', {
							active: sortState.field === SortType.SUCCESS_RATE && sortState.direction === sortDirections.ASC
						})}
						onClick={() => handleSortOptionSelect(SortType.SUCCESS_RATE, sortDirections.ASC)}>
						Soru Sayısı (Çoktan Aza)
					</button>
					<button
						className={clsx('student-performance__sort-option', {
							active: sortState.field === SortType.SUCCESS_RATE && sortState.direction === sortDirections.DESC
						})}
						onClick={() => handleSortOptionSelect(SortType.SUCCESS_RATE, sortDirections.DESC)}>
						Soru Sayısı (Azdan Çoka)
					</button>
				</div>
			</DialogModal>
		</div>
	);
};

export default StudentPerformance;
